import React from 'react';
import { Skeleton } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useGetEvaluationTools } from './hooks/useGetEvaluationTools';
import { RiskBadge } from '@/components/views/Resolution886/components/RiskBadge';
import { ErgonomicRiskBadge } from '../../../../ErgonomicTools/ErgonomicRiskBadge';
import type { RelatedReport } from '@/context/Resolution886Report/types';

import * as S from './styles';

interface RiskEvaluationData {
	risk_level: string;
	technical_support: {
		category: string;
		tool: string;
		category_key: string;
		risk: RelatedReport;
	};
	observations: string;
}

interface RiskEvaluationProps {
	isLoading?: boolean;
	data: RiskEvaluationData;
}

export function RiskEvaluation({ isLoading = false, data }: Readonly<RiskEvaluationProps>): JSX.Element {
	const { tools } = useGetEvaluationTools(data.technical_support.category_key);

	if (isLoading) {
		return (
			<div style={{ marginBottom: '24px', marginTop: '24px' }}>
				<S.RiskEvaluationTitle>{I18n.get('Risk evaluation')}</S.RiskEvaluationTitle>
				<S.RiskEvaluationTable>
					<thead>
						<tr>
							<th>{I18n.get('Risk level')}</th>
							<th>{I18n.get('Technical support')}</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
								<div style={{ display: 'flex', justifyContent: 'center' }}>
									<Skeleton.Button active size="default" style={{ width: '100px', height: '24px' }} />
								</div>
							</td>
							<td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
								<div style={{ display: 'flex', justifyContent: 'center' }}>
									<Skeleton.Button active size="default" style={{ width: '100px', height: '24px' }} />
								</div>
							</td>
						</tr>
					</tbody>
				</S.RiskEvaluationTable>

				<S.RiskEvaluationTable>
					<thead>
						<tr>
							<th>{I18n.get('Observations')}</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<Skeleton.Input active size="default" style={{ width: '100%', height: '60px' }} />
							</td>
						</tr>
					</tbody>
				</S.RiskEvaluationTable>
			</div>
		);
	}

	return (
		<div style={{ marginBottom: '24px', marginTop: '24px' }}>
			<S.RiskEvaluationTitle>{I18n.get('Risk evaluation')}</S.RiskEvaluationTitle>
			<S.RiskEvaluationTable>
				<thead>
					<tr>
						<th>{I18n.get('Risk level')}</th>
						{tools?.name &&<th>{I18n.get('Technical support')}</th>}
					</tr>
				</thead>
				<tbody>
					<tr>
						<td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
							<div style={{ display: 'flex', justifyContent: 'center' }}>
								<RiskBadge risk={data?.risk_level} />
							</div>
						</td>
						{tools?.name &&
							<td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
								<div
									style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
								>
									<span style={{ fontWeight: 'bold' }}>{I18n.get('Lifting loads')}</span>
									<span>:</span>
									<span>{tools?.name}</span>
									<ErgonomicRiskBadge relatedReport={data?.technical_support?.risk} />
								</div>
							</td>
						}
					</tr>
				</tbody>
			</S.RiskEvaluationTable>
			<S.RiskEvaluationTable>
				<thead>
					<tr>
						<th>{I18n.get('Observations')}</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>
							<S.ObservationText>{data?.observations}</S.ObservationText>
						</td>
					</tr>
				</tbody>
			</S.RiskEvaluationTable>
		</div>
	);
}
